import type { Monaco } from "@monaco-editor/react";

/**
 * Monaco wiring for the settings pages where an admin writes a TypeScript
 * generator script — the Hogia person-file template (`PersonalfilPage`). The
 * script's context types are served by the backend and loaded into Monaco so
 * the author gets type checking, autocomplete and hover docs.
 */

/**
 * The served type definitions are a module (`export interface …`). Scripts refer
 * to the types without importing them, so the definitions are loaded as globals
 * by dropping the `export` keywords.
 */
export const asGlobalDeclarations = (types: string) =>
	types.replace(/^export (interface|type) /gm, "$1 ");

/**
 * The TypeScript defaults the script editors use: ES2020, semantic + syntax
 * diagnostics on (that's the whole point — the author should see type errors
 * before saving) and eager model sync so a freshly opened editor is checked
 * immediately.
 *
 * `noImplicitAny` is on (while the rest of `strict` stays off): the invoice
 * context types the organisation's custom fields as exact interfaces
 * (`CustomerCustomFields` …), and without it a bracket access to a key that
 * doesn't exist (`customFields["missing"]`) or with a computed key
 * (`customFields[someVar]`) silently becomes `any` instead of an error. It also
 * asks for annotated function parameters. job-service type-checks the script on
 * save with the same options, so the squiggles match what the save rejects.
 */
export function configureScriptEditorTypescript(monaco: Monaco) {
	const ts = monaco.languages.typescript;

	ts.typescriptDefaults.setCompilerOptions({
		target: ts.ScriptTarget.ES2020,
		module: ts.ModuleKind.ESNext,
		moduleResolution: ts.ModuleResolutionKind.NodeJs,
		lib: ["es2020"],
		strict: false,
		noImplicitAny: true,
		noEmit: true,
		allowNonTsExtensions: true,
	});
	ts.typescriptDefaults.setDiagnosticsOptions({
		noSemanticValidation: false,
		noSyntaxValidation: false,
	});
	ts.typescriptDefaults.setEagerModelSync(true);
}

/**
 * Registers one editor's context types under its own `filePath`, keeping every
 * other extra lib in place — the alternative (`setExtraLibs` with just this
 * one) would silently drop the other editor's types if both are ever mounted.
 */
export function setScriptContextTypes(monaco: Monaco, filePath: string, types: string) {
	const { typescriptDefaults } = monaco.languages.typescript;
	const others = Object.entries(typescriptDefaults.getExtraLibs())
		.filter(([path]) => path !== filePath)
		.map(([path, lib]) => ({ filePath: path, content: lib.content }));

	typescriptDefaults.setExtraLibs([...others, { filePath, content: asGlobalDeclarations(types) }]);
}
